import React, { useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../data/products';
import { useProductsStore } from '../store/productsStore';
import { useStore } from '../store/useStore';
import { CarHeartIcon, CarCartIcon } from '../components/CarIcons';
import SEOHead from '../components/SEOHead';
import ReviewSection from '../components/ReviewSection';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToCart, toggleFavorite, favorites } = useStore();
  const products = useProductsStore((state) => state.products);

  const product = useMemo(() => {
    return products.find((p) => p.id === Number(id));
  }, [products, id]);

  // Track recently viewed
  useEffect(() => {
    if (product) {
      const recentlyViewed = JSON.parse(localStorage.getItem('trax-recently-viewed') || '[]');
      const filtered = recentlyViewed.filter((item: any) => item.id !== product.id);
      filtered.unshift({
        id: product.id,
        name: product.name,
        image: product.images[0],
        price: product.salePrice > 0 ? product.salePrice : product.regularPrice,
        viewedAt: Date.now(),
      });
      localStorage.setItem('trax-recently-viewed', JSON.stringify(filtered.slice(0, 20)));
    }
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Ürün bulunamadı</h2>
          <Link to="/magaza" className="text-[#E31E24] hover:underline">Mağazaya Dön</Link>
        </div>
      </div>
    );
  }

  const isFav = favorites.includes(product.id);
  const discount = product.regularPrice > 0 && product.salePrice > 0
    ? Math.round((1 - product.salePrice / product.regularPrice) * 100)
    : 0;
  const displayPrice = product.salePrice > 0 ? product.salePrice : product.regularPrice;

  return (
    <>
      <SEOHead
        title={`${product.name} | T-RaX Car Care`}
        description={product.description}
        image={product.images[0]}
        url={`https://traxcarcareproducts.com/urun/${product.id}`}
        type="product"
        product={product}
      />

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav className="flex items-center gap-2 text-xs text-gray-400">
              <Link to="/" className="hover:text-[#E31E24] transition-colors">{t('nav.home')}</Link>
              <span>/</span>
              <Link to="/magaza" className="hover:text-[#E31E24] transition-colors">{t('nav.shop')}</Link>
              <span>/</span>
              <span className="text-gray-600 truncate max-w-[200px]">{product.name}</span>
            </nav>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Product Image */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8">
              <div className="relative">
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-full h-auto max-h-[500px] object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"><rect fill="%23f8f9fa" width="400" height="400" rx="12"/><text x="200" y="210" text-anchor="middle" fill="%236c757d" font-size="16" font-family="Arial">T-RaX</text></svg>`)}`;
                  }}
                />
                {discount > 0 && (
                  <span className="absolute top-4 left-4 bg-[#E31E24] text-white text-sm font-bold px-3 py-1.5 rounded-full shadow-lg">
                    %{discount} {t('home.sale')}
                  </span>
                )}
                {product.isNew && (
                  <span className="absolute top-4 right-4 bg-[#FF6B00] text-white text-sm font-bold px-3 py-1.5 rounded-full shadow-lg">
                    {t('home.new')}
                  </span>
                )}
              </div>
            </div>

            {/* Product Info */}
            <div className="flex flex-col">
              {/* Brand */}
              <span className="text-xs font-bold text-[#E31E24] uppercase tracking-wider mb-2">
                {product.brand}
              </span>

              <h1 className="text-2xl sm:text-3xl font-black text-gray-800 mb-4 leading-tight">
                {product.name}
              </h1>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-4">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg
                      key={star}
                      className={`w-4 h-4 ${star <= Math.round(product.rating) ? 'text-amber-400' : 'text-gray-200'}`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="text-sm text-gray-500">
                  {product.rating} ({product.reviewCount} {t('reviews.reviews', { defaultValue: 'değerlendirme' })})
                </span>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-6">
                {displayPrice > 0 ? (
                  <>
                    <span className="text-3xl font-black text-[#E31E24]">{formatPrice(displayPrice)} ₺</span>
                    {discount > 0 && (
                      <span className="text-lg text-gray-400 line-through">{formatPrice(product.regularPrice)} ₺</span>
                    )}
                  </>
                ) : (
                  <span className="text-lg text-gray-400">Fiyat bilgisi için iletişime geçin</span>
                )}
              </div>

              {/* Stock Status */}
              <div className="mb-6">
                {product.inStock ? (
                  product.stock <= 10 ? (
                    <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-4 py-2 rounded-xl text-sm font-medium">
                      <span className="animate-pulse">⚠️</span>
                      Son {product.stock} adet kaldı! Hızlı sipariş verin.
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-2 rounded-xl text-sm font-medium">
                      ✅ Stokta mevcut
                    </div>
                  )
                ) : (
                  <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-2 rounded-xl text-sm font-medium">
                    ❌ Tükendi
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="mb-6">
                <h3 className="text-sm font-bold text-gray-800 mb-2 uppercase tracking-wider">
                  {t('product.description', { defaultValue: 'Ürün Açıklaması' })}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">{product.description}</p>
              </div>

              {/* Categories */}
              <div className="mb-6">
                <h3 className="text-sm font-bold text-gray-800 mb-2 uppercase tracking-wider">
                  {t('product.categories', { defaultValue: 'Kategoriler' })}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {product.categories.map((cat) => (
                    <Link
                      key={cat}
                      to={`/magaza?kategori=${cat}`}
                      className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full hover:bg-[#E31E24]/10 hover:text-[#E31E24] transition-colors"
                    >
                      {cat.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-auto">
                <button
                  onClick={() => toggleFavorite(product.id)}
                  className={`flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all duration-300 ${
                    isFav
                      ? 'bg-[#E31E24] text-white shadow-lg shadow-[#E31E24]/30'
                      : 'bg-gray-100 text-gray-600 hover:bg-[#E31E24]/10 hover:text-[#E31E24]'
                  }`}
                >
                  <CarHeartIcon size={18} />
                  {isFav ? t('home.removeFromFav') : t('home.addToFav')}
                </button>
                <button
                  onClick={() => {
                    if (product.inStock) {
                      addToCart(product);
                    }
                  }}
                  disabled={!product.inStock}
                  className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all duration-300 ${
                    product.inStock
                      ? 'bg-gradient-to-r from-[#E31E24] to-[#FF4500] text-white hover:shadow-lg hover:shadow-[#E31E24]/30 active:scale-95'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <CarCartIcon size={18} />
                  {product.inStock ? t('home.addToCart') : 'Tükendi'}
                </button>
              </div>

              {/* Shipping Info */}
              <div className="mt-6 p-4 bg-gray-50 rounded-xl text-xs text-gray-500 space-y-1">
                <p>🚚 1000 TL ve üzeri alışverişlerde <span className="font-bold text-green-600">ücretsiz kargo</span></p>
                <p>📦 Kargo ücreti: 129 TL</p>
                <p>🔒 Güvenli 3D Secure ödeme</p>
                <p>↩️ 14 gün içinde iade hakkı</p>
              </div>
            </div>
          </div>

          {/* Reviews */}
          <ReviewSection productId={product.id} productName={product.name} />
        </div>
      </div>
    </>
  );
};

export default ProductDetailPage;
